/**
 * Global theme — light mode only. Ignores device preference and stored dark/system values.
 */
import { Injectable, signal, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { StorageKeys } from '../constants';
import { ThemeMode } from '../enums';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  /** Always false — dark mode is disabled app-wide. */
  readonly isDark = signal(false);

  constructor() {
    this.initialize();
  }

  /** Idempotent init — safe if called again (e.g. lazy roots). */
  initialize(): void {
    this.applyLightMode();
  }

  getPreference(): ThemeMode {
    return ThemeMode.Light;
  }

  /** No-op — dark mode is disabled. */
  toggle(): void {
    this.applyLightMode();
  }

  /** Always applies light mode regardless of requested preference. */
  setPreference(_mode: ThemeMode): void {
    this.applyLightMode();
  }

  private applyLightMode(): void {
    this.isDark.set(false);
    const root = this.document.documentElement;
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
    try {
      localStorage.setItem(StorageKeys.THEME, ThemeMode.Light);
    } catch {
      // private browsing / quota
    }
  }
}
